import { rtdbNFLService as NFLService } from "../rtdb";
import { NFL_LEAGUES } from "../constants/leagues";
import { USA } from "../constants/countries";

const LIVE_STATUS_CODES = ["Q1", "Q2", "Q3", "Q4", "HT", "OT"];
const FINISHED_STATUS_CODES = ["FT", "AOT"];

const DEFAULT_TIMEZONE = "America/New_York";

const getCurrentSeason = () =>
  typeof NFLService.getCurrentSeason === "function"
    ? NFLService.getCurrentSeason()
    : new Date().getFullYear();

const getTodayDate = () =>
  typeof NFLService.getTodayDate === "function"
    ? NFLService.getTodayDate()
    : new Date().toISOString().split("T")[0];

const parseSeason = (season) => {
  if (!season) return getCurrentSeason();
  const numeric = parseInt(season, 10);
  return Number.isNaN(numeric) ? getCurrentSeason() : numeric;
};

export const getDefaultFilters = () => ({
  date: getTodayDate(),
  league: String(NFL_LEAGUES[0]?.id || 1),
  season: String(getCurrentSeason()),
  country: "",
  conference: "",
  division: ""
});

export const getLeagues = () => NFL_LEAGUES;

export const getSeasons = () =>
  typeof NFLService.getAvailableSeasons === "function"
    ? NFLService.getAvailableSeasons()
    : [getCurrentSeason()];

export const getConferences = async (league, season) => {
  try {
    const response = await NFLService.getConferences(
      Number(league) || 1,
      parseSeason(season)
    );
    return response?.response || [];
  } catch (error) {
    console.error("Failed to load NFL conferences:", error);
    return [];
  }
};

export const getDivisions = async (league, season) => {
  try {
    const response = await NFLService.getDivisions(
      Number(league) || 1,
      parseSeason(season)
    );
    return response?.response || [];
  } catch (error) {
    console.error("Failed to load NFL divisions:", error);
    return [];
  }
};

const sortGamesByStatus = (games = []) => {
  return [...games].sort((a, b) => {
    const aStatus = a?.game?.status?.short;
    const bStatus = b?.game?.status?.short;
    const aLive = LIVE_STATUS_CODES.includes(aStatus);
    const bLive = LIVE_STATUS_CODES.includes(bStatus);

    if (aLive && !bLive) return -1;
    if (!aLive && bLive) return 1;

    const aFinished = FINISHED_STATUS_CODES.includes(aStatus);
    const bFinished = FINISHED_STATUS_CODES.includes(bStatus);

    if (aLive && bLive) {
      const statusOrder = {
        Q1: 1,
        Q2: 2,
        HT: 3,
        Q3: 4,
        Q4: 5,
        OT: 6
      };
      return (statusOrder[aStatus] || 7) - (statusOrder[bStatus] || 7);
    }

    if (aFinished && !bFinished) return 1;
    if (!aFinished && bFinished) return -1;

    const aDate = a?.game?.date
      ? new Date(a.game.date).getTime()
      : a?.game?.date?.timestamp || 0;
    const bDate = b?.game?.date
      ? new Date(b.game.date).getTime()
      : b?.game?.date?.timestamp || 0;

    return aDate - bDate;
  });
};

const filterGamesByStatus = (games = [], status) => {
  if (status === "live") {
    return games.filter((game) => LIVE_STATUS_CODES.includes(game?.game?.status?.short));
  }
  if (status === "upcoming") {
    return games.filter((game) => game?.game?.status?.short === "NS");
  }
  return games;
};

export const getGames = async ({
  date,
  league,
  season,
  country,
  teamId,
  weekId,
  status
}) => {
  const normalizedSeason = parseSeason(season);
  const normalizedLeague = Number(league) || 1;
  const normalizedTeam = teamId ? Number(teamId) : null;
  const finalDate = date || getTodayDate();

  const response = weekId
    ? await NFLService.getGamesForWeek(
        weekId,
        normalizedSeason,
        normalizedLeague,
        normalizedTeam,
        DEFAULT_TIMEZONE
      )
    : await NFLService.getGames(
        null,
        finalDate,
        normalizedLeague,
        normalizedSeason,
        normalizedTeam,
        null,
        null,
        DEFAULT_TIMEZONE
      );

  let games = response?.response || [];

  if (country) {
    const normalizedCountry = country.toLowerCase();
    games = games.filter((game) => {
      const leagueCountry = game?.league?.country?.toLowerCase();
      const flagCountry = game?.league?.flag?.toLowerCase();
      return (
        leagueCountry === normalizedCountry ||
        flagCountry === normalizedCountry ||
        normalizedCountry === USA.code.toLowerCase()
      );
    });
  }

  if (!weekId && !date) {
    games = games.filter((game) => game?.game?.date?.date === finalDate || !game?.game?.date?.date);
  }

  const filteredByStatus = filterGamesByStatus(games, status);
  return sortGamesByStatus(filteredByStatus);
};

export const getStandings = async ({
  league,
  season,
  conference,
  division
}) => {
  const response = await NFLService.getStandings(
    Number(league) || 1,
    parseSeason(season),
    null,
    conference || null,
    division || null
  );

  return response?.response || [];
};

export const getTeams = async ({ league, season, conference }) => {
  let teams = [];

  if (Number(league) === 1) {
    try {
      const standings = await getStandings({
        league,
        season,
        conference: "",
        division: ""
      });
      teams = standings
        .map((standing) => ({
          id: standing?.team?.id,
          name: standing?.team?.name,
          logo: standing?.team?.logo,
          code: standing?.team?.code,
          conference:
            NFLService.getConferenceName?.(standing?.conference) ||
            standing?.conference,
          division: standing?.division,
          city: standing?.team?.city,
          stadium: standing?.team?.venue?.name
        }))
        .filter((team) => team.id);
    } catch (error) {
      console.warn("Failed to derive teams from standings, falling back:", error);
    }
  }

  if (teams.length === 0) {
    try {
      const response = await NFLService.getTeams(
        null,
        Number(league) || 1,
        parseSeason(season)
      );
      teams =
        response?.response?.map((team) => ({
          id: team?.id,
          name: team?.name,
          logo: team?.logo,
          code: team?.code,
          conference:
            NFLService.getConferenceName?.(team?.conference) || team?.conference,
          division: team?.division,
          city: team?.city || team?.name,
          stadium: team?.stadium
        })) || [];
    } catch (error) {
      console.error("Failed to load NFL teams:", error);
      teams = [];
    }
  }

  if (conference) {
    const normalizedConference = conference.toLowerCase();
    teams = teams.filter((team) =>
      (team?.conference || "")
        .toString()
        .toLowerCase()
        .includes(normalizedConference)
    );
  }

  return teams;
};

export default {
  getDefaultFilters,
  getLeagues,
  getSeasons,
  getGames,
  getStandings,
  getTeams,
  getConferences,
  getDivisions,
  getTodayDate
};
